import React, { useState, useCallback, useEffect, useRef } from "react";
import "./MathPractice.css";
import { awardToken } from "../../awardtoken.js";

// Place sound files in /public/sounds/correct.mp3 etc.
const SOUND_SOURCES = [
  "/sounds/correct.mp3",
  "/sounds/correct.ogg",
  "/sounds/correct.wav",
];

const DIGIT_OPTIONS = [
  { label: "Single-digit denominators", value: 1 },
  { label: "Double-digit denominators", value: 2 },
];

const SHEET_SIZES = [10, 25, 50];

/* ────────── Fraction Problem Generators ────────── */
function randIntInclusive(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function gcd(a, b) {
  return b === 0 ? a : gcd(b, a % b);
}
function simplifyFrac(num, den) {
  const d = gcd(Math.abs(num), Math.abs(den));
  return { num: num / d, den: den / d };
}

function generateFraction(digits) {
  const den = digits === 1
    ? randIntInclusive(2, 9)
    : randIntInclusive(10, 20); // 2-digit denominators, cap at 20 for easy printing
  let num = randIntInclusive(1, den - 1);
  return simplifyFrac(num, den);
}

function Fraction({ numer, denom, size = "2.2rem", style = {} }) {
  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        fontSize: size,
        lineHeight: 1.12,
        fontWeight: 600,
        minWidth: "2.4em",
        ...style,
      }}
    >
      <span style={{ textAlign: "center" }}>{numer}</span>
      <span
        style={{
          borderBottom: "2.2px solid #3085c3",
          minWidth: "1.8em",
          display: "block",
          margin: "0.04em 0",
        }}
      ></span>
      <span style={{ textAlign: "center" }}>{denom}</span>
    </div>
  );
}

function generateProblem(digits) {
  const f1 = generateFraction(digits);
  const f2 = generateFraction(digits);
  // Force like denominators for beginner
  return { a: f1, b: { num: f2.num, den: f1.den } };
}
function generateProblemSet(count, digits) {
  return Array.from({ length: count }, () => generateProblem(digits));
}

/* ─────── Fraction Problem Renderer ─────── */
function FractionProblem({ a, b, showAnswer = false }) {
  const answer = simplifyFrac(a.num + b.num, a.den);
  return (
    <div
      className="fraction-problem"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        fontSize: "2rem",
        gap: "1.3rem",
        margin: "2.2rem 0 2rem 0"
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "1.7rem" }}>
        <Fraction numer={a.num} denom={a.den} />
        <span style={{ fontSize: "2.1rem", fontWeight: 600, margin: "0 0.6rem" }}>+</span>
        <Fraction numer={b.num} denom={b.den} />
      </div>
      {showAnswer && (
        <div style={{ marginTop: "1.2rem" }}>
          <span style={{ fontSize: "1.5rem", marginRight: "0.7em", color: "#2688e6" }}>Answer:</span>
          <Fraction numer={answer.num} denom={answer.den} size="1.8rem" />
        </div>
      )}
    </div>
  );
}

function WorksheetColumnProblem({ a, b }) {
  return (
    <div className="math-col" style={{ alignItems: "center", textAlign: "center" }}>
      <div className="math-top" style={{ marginBottom: "0.35em" }}>
        <Fraction numer={a.num} denom={a.den} size="1.2rem" />
      </div>
      <div className="math-bottom" style={{ marginBottom: "0.25em" }}>
        + <Fraction numer={b.num} denom={b.den} size="1.2rem" />
      </div>
      <div className="math-underline" />
      <div className="math-answer-space">&nbsp;</div>
    </div>
  );
}


/* ────────── MAIN COMPONENT ────────── */
export default function MathFractionPractice({ onAwardTokens } = {}) {
  const [digitCount, setDigitCount] = useState(1);
  const [current, setCurrent] = useState(() => generateProblem(1));
  const [numAnswer, setNumAnswer] = useState("");
  const [denAnswer, setDenAnswer] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [sheetProblems, setSheetProblems] = useState(null);
  const correctSoundRef = useRef(null);
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const tokensAwarded = useRef(0);

  useEffect(() => {
    if (correct >= (tokensAwarded.current + 1) * 10) {
      awardToken();
      tokensAwarded.current += 1;
      if (onAwardTokens) onAwardTokens(1);
    }
  }, [correct, onAwardTokens]);

  useEffect(() => {
    const audio = new Audio();
    for (const src of SOUND_SOURCES) {
      const type = src.endsWith(".mp3")
        ? "audio/mpeg"
        : src.endsWith(".ogg")
        ? "audio/ogg"
        : "audio/wav";
      if (audio.canPlayType(type)) {
        audio.src = src;
        break;
      }
    }
    correctSoundRef.current = audio;
  }, []);

  const resetPractice = useCallback(() => {
    setAttempts(0);
    setCorrect(0);
    setCurrent(generateProblem(digitCount));
    setNumAnswer("");
    setDenAnswer("");
  }, [digitCount]);

  useEffect(() => {
    resetPractice();
  }, [digitCount, resetPractice]);

  const handleAnswerSubmit = (e) => {
    e.preventDefault();
    if (
      numAnswer.trim() === "" ||
      denAnswer.trim() === "" ||
      waitingForNext
    )
      return;
    const student = simplifyFrac(Number(numAnswer), Number(denAnswer));
    const answer = simplifyFrac(current.a.num + current.b.num, current.a.den);

    setAttempts((n) => n + 1);
    if (
      student.num === answer.num &&
      student.den === answer.den
    ) {
      setCorrect((n) => n + 1);
      if (correctSoundRef.current) {
        correctSoundRef.current.currentTime = 0;
        correctSoundRef.current.play();
      }
      setFeedback(null);
      setCurrent(generateProblem(digitCount));
      setNumAnswer("");
      setDenAnswer("");
    } else {
      setFeedback(
        `❌ The correct answer is: ${answer.num} / ${answer.den}`
      );
      setWaitingForNext(true);
    }
  };

  const startSheetMode = (count) => {
    const problems = generateProblemSet(count, digitCount);
    setSheetProblems(problems);
    setTimeout(() => window.print(), 100);
  };
  const leaveSheetMode = () => setSheetProblems(null);

  if (sheetProblems) {
    return (
      <div className="worksheet">
        <h2 className="no-print">Fraction Addition Worksheet – {digitCount === 1 ? "1-digit" : "2-digit"} denominator</h2>
        <button className="no-print" onClick={leaveSheetMode}>
          Back to Practice
        </button>
        <div className="sheet-grid">
          {sheetProblems.map((p, idx) => (
            <WorksheetColumnProblem key={idx} {...p} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="practice-container">
      <h2>Fraction Addition Practice</h2>
      <fieldset>
        <legend>Choose denominator size:</legend>
        {DIGIT_OPTIONS.map(({ label, value }) => (
          <label key={value} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              checked={digitCount === value}
              onChange={() => setDigitCount(value)}
            />{" "}
            {label}
          </label>
        ))}
      </fieldset>
      <FractionProblem a={current.a} b={current.b} />

      <form onSubmit={handleAnswerSubmit} className="answer-form">
        <span>Your answer:</span>
        <input
          type="number"
          placeholder="Numerator"
          value={numAnswer}
          onChange={e => setNumAnswer(e.target.value)}
          style={{ width: 70, margin: "0 7px" }}
          disabled={waitingForNext}
        />
        <span>/</span>
        <input
          type="number"
          placeholder="Denominator"
          value={denAnswer}
          onChange={e => setDenAnswer(e.target.value)}
          style={{ width: 70, margin: "0 7px" }}
          disabled={waitingForNext}
        />
        <button
          type="submit"
          style={{ marginLeft: "0.7rem" }}
          disabled={waitingForNext}
        >
          Submit
        </button>
      </form>

      {feedback && (
        <div className="math-practice-feedback" style={{ marginBottom: 7 }}>
          {feedback}
        </div>
      )}

      {waitingForNext && (
        <button
          className="math-pill-btn"
          style={{ marginTop: "0.7em" }}
          onClick={() => {
            setCurrent(generateProblem(digitCount));
            setNumAnswer("");
            setDenAnswer("");
            setFeedback(null);
            setWaitingForNext(false);
          }}
        >
          Next
        </button>
      )}

      <p>
        Score: {correct} / {attempts}
      </p>

      <div className="worksheet-buttons">
        <strong>Printable worksheets:</strong>
        {SHEET_SIZES.map((cnt) => (
          <button
            key={cnt}
            onClick={() => startSheetMode(cnt)}
            style={{ marginLeft: "0.5rem" }}
          >
            {cnt} Questions
          </button>
        ))}
      </div>
    </div>
  );
}
